import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, TrendingUp, Star } from 'lucide-react';
import { miniSimulators } from '../../data/mock';

const SimulatorsSection = () => {
  const popularSimulators = miniSimulators.filter(sim => sim.popular).slice(0, 6);

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-16">
          <div className="max-w-2xl mb-8 lg:mb-0">
            
            <h2 className="text-4xl md:text-5xl font-display font-black text-gray-900 mb-6">
              Simulateurs
              <br />
              <span className="gradient-text">ultra-précis</span>
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Testez tous vos scénarios avec nos simulateurs basés sur les données fiscales 2026 certifiées. 
              Résultats instantanés, 100% fiables.
            </p>
          </div>
          
          <Link to="/simulateurs">
            <Button 
              variant="outline" 
              size="lg"
              className="btn-modern border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50 px-8 py-3 rounded-xl group"
            >
              Tous les simulateurs
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Grille des simulateurs */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {popularSimulators.map((simulator, index) => (
            <Card 
              key={simulator.id} 
              className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 bg-white border-0 animate-fadeInUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardHeader className="pb-4">
                {/* Badge populaire - au-dessus du contenu sur mobile */}
                {simulator.popular && (
                  <div className="mb-3">
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 font-semibold">
                      🔥 Populaire
                    </Badge>
                  </div>
                )}
                
                <div className="flex items-center gap-4">
                  {/* Icône avec effet de glow */}
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <span className="text-3xl group-hover:scale-125 transition-transform">
                      {simulator.icon}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                      {simulator.title}
                    </CardTitle>
                    <Badge 
                      variant="secondary" 
                      className="bg-gray-100 text-gray-700 font-mono text-xs"
                    >
                      {simulator.category}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <CardDescription className="text-gray-600 mb-6 leading-relaxed">
                  {simulator.description}
                </CardDescription>
                
                <Link to={`/simulateur/${simulator.id}`} className="block">
                  <Button 
                    className="btn-modern w-full gradient-primary text-white py-3 rounded-xl font-semibold group hover:shadow-lg"
                  >
                    <TrendingUp className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                    Simuler maintenant
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>

              {/* Effet de brillance */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000"></div>
              </div>
            </Card>
          ))}
        </div>

        {/* Call to action en bas */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-4 px-8 py-8 rounded-3xl glass border border-white/20 backdrop-blur-lg">
            <div className="text-lg font-semibold text-gray-900">
              Besoin d'aller plus loin ?
            </div>
            <p className="text-gray-600 max-w-md">
              Découvrez notre simulateur complet avec tous les scénarios avancés et l'accompagnement personnalisé
            </p>
            <Button 
              className="btn-modern gradient-primary text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg group"
              onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
            >
              Accéder au simulateur complet
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SimulatorsSection;